import { memo, useState } from 'react';
import { Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { Button, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { EventDetailMoreMenu } from '@/features/guardian/components/event-detail/event-detail-more-menu';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

const strings = STRINGS.guardian.kids.eventDetail;

export type EventDetailSurface = 'screen' | 'panel' | 'popup';

type EventDetailHeaderProps = {
  title: string;
  surface: EventDetailSurface;
  onClose: () => void;
  onShare?: () => void;
  showMore?: boolean;
};

function EventDetailHeaderComponent({
  title,
  surface,
  onClose,
  onShare,
  showMore = true,
}: EventDetailHeaderProps) {
  const { scale } = useScaling();
  const { top } = useSafeAreaInsets();
  const styles = useStyles();
  const [isMenuVisible, setMenuVisible] = useState(false);

  return (
    <Stack
      direction="row"
      align="center"
      gap={scale(12)}
      style={[
        styles.header,
        surface === 'screen' ? styles.screenHeader : styles.headerDivider,
        surface === 'screen' ? { paddingTop: top + scale(8) } : null,
      ]}>
      {surface === 'screen' ? (
        <Button variant="ghost" onPress={onClose} style={styles.backButton}>
          <Stack
            viewType="liquid-glass"
            align="center"
            justify="center"
            backgroundColor={withOpacity(colors['0'], 0.3)}
            overlayColor={withOpacity(colors['0'], 0.3)}
            intensity={20}
            tint="light"
            style={styles.backButtonGlass}>
            <Button.Icon
              name="IcXClose"
              color={guardianColors['6']}
              styleWrapper={styles.backButtonIcon}
            />
          </Stack>
        </Button>
      ) : (
        <Pressable
          accessibilityLabel={strings.close}
          accessibilityRole="button"
          hitSlop={scale(12)}
          onPress={onClose}
          style={({ pressed }) => [styles.iconButton, pressed ? styles.pressed : null]}>
          <SvgIcon name="IcXClose" size={scale(22)} color={guardianColors['6']} />
        </Pressable>
      )}
      <Text
        numberOfLines={1}
        variant="Font Guardian App/Heading/Heading H6 18px"
        style={styles.title}>
        {title}
      </Text>
      {showMore ? (
        <Stack position="relative">
          <Pressable
            accessibilityLabel={strings.more}
            accessibilityRole="button"
            hitSlop={scale(12)}
            onPress={() => setMenuVisible(value => !value)}
            style={({ pressed }) => [
              surface === 'screen' ? styles.backButton : styles.iconButton,
              pressed ? styles.pressed : null,
            ]}>
            <Stack
              viewType={surface === 'screen' ? 'liquid-glass' : 'view'}
              direction="row"
              align="center"
              justify="center"
              gap={scale(3)}
              backgroundColor={surface === 'screen' ? withOpacity(colors['0'], 0.3) : undefined}
              overlayColor={surface === 'screen' ? withOpacity(colors['0'], 0.3) : undefined}
              intensity={20}
              tint="light"
              style={surface === 'screen' ? styles.backButtonGlass : undefined}>
              <Stack
                width={scale(3)}
                height={scale(3)}
                borderRadius={scale(999)}
                style={styles.dot}
              />
              <Stack
                width={scale(3)}
                height={scale(3)}
                borderRadius={scale(999)}
                style={styles.dot}
              />
              <Stack
                width={scale(3)}
                height={scale(3)}
                borderRadius={scale(999)}
                style={styles.dot}
              />
            </Stack>
          </Pressable>
          <EventDetailMoreMenu
            visible={isMenuVisible}
            onDismiss={() => setMenuVisible(false)}
            onShare={onShare}
          />
        </Stack>
      ) : (
        <Stack width={scale(40)} height={scale(40)} />
      )}
    </Stack>
  );
}

export const EventDetailHeader = memo(EventDetailHeaderComponent);

const useStyles = createStyles(({ scale }) => ({
  header: {
    minHeight: scale(56),
    paddingHorizontal: scale({ phone: 20, tablet: 12 }),
    paddingVertical: scale(8),
    backgroundColor: colors['0'],
    zIndex: 20,
  },
  headerDivider: {
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['2'],
  },
  screenHeader: {
    backgroundColor: guardianColors['1'],
  },
  iconButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    borderCurve: 'continuous',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['1'],
  },
  backButton: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    padding: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    shadowColor: 'transparent',
    elevation: 0,
  },
  backButtonGlass: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(20),
    paddingTop: scale(10),
    paddingRight: scale(12),
    paddingBottom: scale(10),
    paddingLeft: scale(10),
  },
  backButtonIcon: {
    marginRight: 0,
  },
  title: {
    flex: 1,
    color: guardianColors['10'],
  },
  dot: {
    backgroundColor: guardianColors['6'],
  },
  pressed: {
    opacity: 0.72,
  },
}));
